import React, { memo, useMemo } from 'react';
import { Portal } from 'react-portal';
import { FixedSizeGrid } from 'react-window';
import { TFaceListProps, TFaceListTooltipProps } from 'reactApp/ui/FaceFilter/FaceFilter.types';
import { FaceListItem } from 'reactApp/ui/FaceFilter/FaceList/FaceItem/FaceListItem';
import { getGridSizes } from 'reactApp/ui/FaceFilter/FaceList/FaceList.helpers';
import Tooltip from 'reactApp/ui/Tooltip/Tooltip';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { useWindowResizer } from 'reactApp/ui/VirtualList/VirtualList.hooks';

import styles from './FaceList.css';

export const FaceList = memo(function FaceList(props: TFaceListProps) {
    const [rootRef, containerWidth] = useWindowResizer();

    const style = useMemo(() => (props.isMobile ? ({ overflowX: 'hidden' } as const) : ({ overflowX: 'auto' } as const)), [props.isMobile]);

    const { columnCount, columnWidth, rowCount, height, rowHeight, width } = getGridSizes({
        faces: props.faces?.length || 0,
        hasMoreToLoad: props.hasMoreToLoad,
        isMobile: props.isMobile,
        width: containerWidth,
    });

    if (!props.faces?.length) {
        return null;
    }

    return (
        <div className={styles.root} ref={rootRef}>
            <FixedSizeGrid
                columnCount={columnCount}
                columnWidth={columnWidth}
                width={width}
                height={height}
                rowCount={rowCount}
                rowHeight={rowHeight}
                itemData={{ ...props, columnCount }}
                style={style}
            >
                {FaceListItem}
            </FixedSizeGrid>
        </div>
    );
});

export const FaceListTooltip = memo(function FaceListTooltip({ onTooltipClose, targetRef, ...props }: TFaceListTooltipProps) {
    if (!props.faces?.length) {
        return null;
    }

    return (
        <Portal>
            <Tooltip
                target={targetRef?.current}
                fitWindow
                noArrow
                fluid
                placement={tooltipPlacements.BOTTOM}
                onClose={onTooltipClose}
                theme="octavius"
                offset={{ y: 10 }}
                animated
                radius={20}
            >
                <div className={styles.tooltip}>
                    <FaceList {...props} />
                </div>
            </Tooltip>
        </Portal>
    );
});
